import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "./configurePdfWorker";
import "react-pdf/dist/Page/TextLayer.css";
import { VisualAssetReference, buildDocumentFileUrl } from "../../api/documentAgent";

const PDF_OPTIONS = { isEvalSupported: false };

type Props = {
    asset: VisualAssetReference;
    conversationId: string;
};

type RenderSize = { width: number; height: number };

function clamp01(value: number, fallback: number) {
    return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : fallback;
}

export default function InlineVisualAssetPreview({ asset, conversationId }: Props) {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const [hostWidth, setHostWidth] = useState(640);
    const [renderSize, setRenderSize] = useState<RenderSize | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        const element = hostRef.current;
        if (!element) return undefined;
        const measure = () => setHostWidth(Math.max(280, element.clientWidth || 640));
        measure();
        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(measure);
            observer.observe(element);
            return () => observer.disconnect();
        }
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const box = useMemo(() => {
        const left = clamp01(asset.bboxLeft, 0);
        const top = clamp01(asset.bboxTop, 0);
        const right = clamp01(asset.bboxRight, 1);
        const bottom = clamp01(asset.bboxBottom, 1);
        const valid = right - left >= 0.03 && bottom - top >= 0.03;
        return valid ? { left, top, right, bottom } : { left: 0, top: 0, right: 1, bottom: 1 };
    }, [asset]);

    const crop = !asset.useFullPageFallback && box.right - box.left < 0.98 && box.bottom - box.top < 0.98;
    const boxWidth = Math.max(0.08, box.right - box.left);
    // Render a larger whole page so the selected region fills the chat card, but cap it to avoid
    // making a tiny AI box allocate an enormous canvas. Low-confidence/tiny boxes are already full-page.
    const pageWidth = crop
        ? Math.min(1400, Math.max(hostWidth, hostWidth / Math.max(boxWidth, 0.42)))
        : hostWidth;

    const fileUrl = buildDocumentFileUrl(asset.documentId, conversationId);
    const openUrl = `${fileUrl}#page=${Math.max(1, asset.pageNumber)}`;

    const viewportStyle = crop && renderSize
        ? {
            width: Math.min(hostWidth, renderSize.width * boxWidth),
            height: Math.max(120, renderSize.height * (box.bottom - box.top)),
        }
        : undefined;

    return (
        <section className="mt-4 overflow-hidden rounded-xl border border-primary_one-100 bg-black/20">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary_one-100 px-3 py-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">
                        {asset.label || `${asset.assetType} · page ${asset.pageNumber}`}
                    </p>
                    <p className="truncate text-xs text-gray-400">
                        {asset.documentTitle} · p. {asset.pageNumber}
                        {asset.assetGroupKey ? ` · part ${asset.sequenceIndex + 1}` : ""}
                    </p>
                </div>
                <a
                    href={openUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded border border-primary_one-100 px-2 py-1 text-xs font-semibold text-primary_one-200 hover:border-primary_one-200"
                >
                    View full page
                </a>
            </div>

            <div ref={hostRef} className="w-full overflow-auto bg-white p-2">
                {loadFailed ? (
                    <div className="p-4 text-sm text-gray-700">
                        The inline preview could not be rendered. Use “View full page” to open the original PDF.
                    </div>
                ) : (
                    <Document
                        file={fileUrl}
                        options={PDF_OPTIONS}
                        loading={<div className="p-4 text-sm text-gray-700">Loading original visual…</div>}
                        onLoadError={() => setLoadFailed(true)}
                    >
                        {crop ? (
                            <div
                                className="relative overflow-hidden"
                                style={viewportStyle ?? { width: hostWidth, minHeight: 180 }}
                                aria-label={`Cropped ${asset.assetType} from page ${asset.pageNumber}`}
                            >
                                <div
                                    className="absolute left-0 top-0"
                                    style={renderSize ? {
                                        transform: `translate(${-box.left * renderSize.width}px, ${-box.top * renderSize.height}px)`,
                                    } : undefined}
                                >
                                    <Page
                                        pageNumber={Math.max(1, asset.pageNumber)}
                                        width={pageWidth}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        onRenderSuccess={(page: any) => setRenderSize({ width: page.width, height: page.height })}
                                    />
                                </div>
                            </div>
                        ) : (
                            <Page
                                pageNumber={Math.max(1, asset.pageNumber)}
                                width={hostWidth}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onRenderSuccess={(page: any) => setRenderSize({ width: page.width, height: page.height })}
                            />
                        )}
                    </Document>
                )}
            </div>
            {asset.useFullPageFallback && (
                <p className="px-3 py-2 text-xs text-gray-400">
                    Qouda was not confident enough in the crop, so the full source page is shown.
                </p>
            )}
        </section>
    );
}
